import React, { useEffect, useMemo, useState } from 'react';
import { CheckIcon, ImageIcon, SaveIcon, SearchIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SeoChecklist } from '../components/seo/SeoChecklist';
import { SearchTargets } from '../components/seo/SearchTargets';
import { getSearchTargets, type SearchTarget } from '../data/searchTargets';
import { SeoLaunchSteps } from '../components/seo/SeoLaunchSteps';
import { getSeoScore, runSeoChecks } from '../data/seo';
import { getPublishedWebsiteContent, publishWebsiteContent, type WebsiteContent } from '../data/website';
export function SearchVisibility() {
  const [draft, setDraft] = useState<WebsiteContent>(() => getPublishedWebsiteContent());
  const [saved, setSaved] = useState(false);
  const [targets, setTargets] = useState<SearchTarget[]>(() => getSearchTargets());
  const checks = useMemo(() => runSeoChecks(draft), [draft]);
  const score = getSeoScore(checks);
  const needsAttention = checks.filter(check => check.status !== 'pass').length;
  useEffect(() => {
    if (!saved) return undefined;
    const timer = window.setTimeout(() => setSaved(false), 2200);
    return () => window.clearTimeout(timer);
  }, [saved]);
  const update = <Key extends keyof WebsiteContent,>(key: Key, value: WebsiteContent[Key]) => {
    setDraft(current => ({
      ...current,
      [key]: value
    }));
    setSaved(false);
  };
  const save = () => {
    if (publishWebsiteContent(draft)) setSaved(true);
  };
  return <div data-id="element-1719" data-mp="x22j0rhlm1hd">
      <PageHeader eyebrow="Search visibility" title="Get found on Google." subtitle="Everything that decides whether someone searching for your band, your city, or your songs lands on your site." actions={<Button size="sm" onClick={save} data-id="element-1721" data-mp="2yvs35lxxw5ms">
            {saved ? <CheckIcon className="h-4 w-4" data-id="element-1722" data-mp="33rz0wrg6matn" /> : <SaveIcon className="h-4 w-4" data-id="element-1723" data-mp="33t0kjkb9k5mz" />}
            {saved ? 'Saved' : 'Save & publish'}
          </Button>} data-id="element-1720" data-mp="x3dlmnzb536w" />

      <section className="mb-6" aria-labelledby="seo-score" data-id="element-1724" data-mp="5z70jbhqthdo">
        <Card className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:gap-7" data-id="element-1725" data-mp="60rv314250bj">
          <div className="flex items-center gap-4" data-id="element-1726" data-mp="jzri64bx975l">
            <div className="relative flex h-20 w-20 shrink-0 items-center justify-center" data-id="element-1727" data-mp="f054xuw3fqws">
              <svg viewBox="0 0 44 44" className="h-full w-full -rotate-90" aria-hidden="true" data-id="element-1728" data-mp="tx5xg0m3zpwb">
                <circle cx="22" cy="22" r="19" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" data-id="element-1729" data-mp="oy2t6idydnpe" />
                <circle cx="22" cy="22" r="19" fill="none" stroke="#d97742" strokeWidth="4" strokeLinecap="round" strokeDasharray={`${score / 100 * 119.4} 119.4`} data-id="element-1730" data-mp="12p7xhxs9n462" />
              </svg>
              <span className="absolute text-[20px] font-semibold tracking-tight text-white" data-id="element-1731" data-mp="17pvoijghvl0a">{score}</span>
            </div>
            <div data-id="element-1732" data-mp="1r89kugvdt0za">
              <h2 id="seo-score" className="text-[17px] font-semibold text-white" data-id="element-1733" data-mp="1w9ff4j4owwax">
                Search readiness
              </h2>
              <p className="mt-1 max-w-md text-[12.5px] leading-relaxed text-zinc-500" data-id="element-1734" data-mp="1hb4xidpsuzgw">
                {needsAttention === 0 ? 'Everything the site controls is in good shape. Work through the setup steps below next.' : `${needsAttention} item${needsAttention === 1 ? '' : 's'} below would improve how you show up in search.`}
              </p>
            </div>
          </div>
        </Card>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]" data-id="element-1735" data-mp="yjkjcgqeytn0">
        <div className="space-y-6" data-id="element-1736" data-mp="13ljnhstz9ul0">
          <section aria-labelledby="seo-basics" data-id="element-1737" data-mp="13if962mstigm">
            <div className="mb-3" data-id="element-1738" data-mp="pm1ut2dk9x6d">
              <h2 id="seo-basics" className="text-[16px] font-semibold text-white" data-id="element-1739" data-mp="pn3ge0s7e22c">
                What Google shows
              </h2>
              <p className="mt-0.5 text-[12.5px] text-zinc-500" data-id="element-1740" data-mp="mjxbmugbjszz">
                These five fields control your search result and every shared link.
              </p>
            </div>
            <Card className="space-y-3 p-4" data-id="element-1741" data-mp="ctkrv4gbw3uw">
              <Field label="Page title" value={draft.seoTitle} onChange={value => update('seoTitle', value)} helper={`${draft.seoTitle.length}/60 characters · band name, genre, and city`} data-id="element-1742" data-mp="cs9pn92j745w" />
              <label className="block" data-id="element-1743" data-mp="3uyo3z94ppg4t">
                <span className="mb-1.5 block text-[12px] font-medium text-zinc-400" data-id="element-1744" data-mp="3uxmgg56if167">Search description</span>
                <textarea value={draft.seoDescription} onChange={event => update('seoDescription', event.target.value)} rows={3} className="w-full resize-y rounded-xl border border-white/[0.1] bg-ink-800 px-3 py-2.5 text-[13px] leading-relaxed text-white outline-none focus:border-[#d97742]/70 focus:ring-2 focus:ring-[#d97742]/15" data-id="element-1745" data-mp="3q0cs6suuwvjy" />
                <span className="mt-1.5 block text-[11px] text-zinc-600" data-id="element-1746" data-mp="1vs8z03ovayg4">
                  {draft.seoDescription.length}/160 characters · mention tour dates, tickets, music, and your region
                </span>
              </label>
              <div className="grid gap-3 sm:grid-cols-2" data-id="element-1747" data-mp="25ibqu0k966cv">
                <Field label="Home city & state" value={draft.seoLocation} onChange={value => update('seoLocation', value)} data-id="element-1748" data-mp="25jwk7mutzj95" />
                <Field label="Genre" value={draft.seoGenre} onChange={value => update('seoGenre', value)} data-id="element-1749" data-mp="1csv1jbrxj5kv" />
              </div>
              <Field label="Website address" value={draft.siteUrl} onChange={value => update('siteUrl', value)} placeholder="https://coopertyband.com" helper="Always the https:// version — this is the address Google treats as official." data-id="element-1750" data-mp="1hrpw1az9eikt" />
              <Field label="Link preview image URL" icon={ImageIcon} value={draft.shareImageUrl} onChange={value => update('shareImageUrl', value)} helper="Shown when your link is texted or posted. Around 1200 × 630 works best." data-id="element-1751" data-mp="2y8kajwufsytk" />
            </Card>
          </section>

          <section aria-labelledby="seo-targets" data-id="element-1752" data-mp="2dizu2vftdw2n">
            <div className="mb-3" data-id="element-1753" data-mp="1prui4rczvnxm">
              <h2 id="seo-targets" className="text-[16px] font-semibold text-white" data-id="element-1754" data-mp="1pt5kcmqsknmm">
                Searches to win
              </h2>
              <p className="mt-0.5 text-[12.5px] text-zinc-500" data-id="element-1755" data-mp="1kqwqlo63hhjg">
                List what people would actually type. Each one shows how honest your chances are and what words are
                missing from the site.
              </p>
            </div>
            <SearchTargets content={draft} targets={targets} onChange={setTargets} data-id="element-1756" data-mp="170kvdge7r2o3" />
          </section>

          <section aria-labelledby="seo-checklist" data-id="element-1757" data-mp="121gpozqhzn2i">
            <div className="mb-3" data-id="element-1758" data-mp="3dpv9e3l0tbhq">
              <h2 id="seo-checklist" className="text-[16px] font-semibold text-white" data-id="element-1759" data-mp="3dplf43sy0i68">
                Site health
              </h2>
              <p className="mt-0.5 text-[12.5px] text-zinc-500" data-id="element-1760" data-mp="3ihwglb3c0c0w">
                Checked against what is actually published. Open any row to see why it matters.
              </p>
            </div>
            <SeoChecklist checks={checks} data-id="element-1761" data-mp="38rbom6i5y5qy" />
          </section>

          <section aria-labelledby="seo-launch" data-id="element-1762" data-mp="ap8danaj519">
            <div className="mb-3" data-id="element-1763" data-mp="8vx1i1qfwmx">
              <h2 id="seo-launch" className="text-[16px] font-semibold text-white" data-id="element-1764" data-mp="5alcd9vzmwgt">
                Setup steps outside the site
              </h2>
              <p className="mt-0.5 text-[12.5px] text-zinc-500" data-id="element-1765" data-mp="3nfesnacnb7d4">
                One-time work that does more for your ranking than anything on the page. Check items off as you go.
              </p>
            </div>
            <SeoLaunchSteps data-id="element-1766" data-mp="2c2m0vzjuuix2" />
          </section>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start" data-id="element-1767" data-mp="2g2w9ljz17nfm">
          <div data-id="element-1768" data-mp="37q7ep41ne4zg">
            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-zinc-400" data-id="element-1769" data-mp="37qqvghfkanbw">
              <SearchIcon className="h-3.5 w-3.5" data-id="element-1770" data-mp="32pjmwg5lhk46" /> Google result preview
            </div>
            <Card className="p-4" data-id="element-1771" data-mp="2yo7xobqmv0wi">
              <div className="truncate text-[12px] text-emerald-300/80" data-id="element-1772" data-mp="2yp9ktekxbdul">{draft.siteUrl || 'https://yourband.com'}</div>
              <div className="mt-1 truncate text-[16px] text-[#8ab4f8]" data-id="element-1773" data-mp="2ts9c0vwovwzs">
                {draft.seoTitle || `${draft.bandName} · Official Site`}
              </div>
              <p className="mt-1.5 line-clamp-3 text-[12.5px] leading-relaxed text-zinc-400" data-id="element-1774" data-mp="3mfvicbxaxfqc">
                {draft.seoDescription || 'Add a description so search results show the right summary.'}
              </p>
            </Card>
          </div>

          <div data-id="element-1775" data-mp="dyihnve71928">
            <div className="mb-2 text-[12px] font-medium text-zinc-400" data-id="element-1776" data-mp="dzabb0o8cwpv">Shared link preview</div>
            <Card className="overflow-hidden p-0" data-id="element-1777" data-mp="97ikqq52qqzq">
              {draft.shareImageUrl ? <img src={draft.shareImageUrl} alt="Link preview" className="h-36 w-full object-cover" data-id="element-1778" data-mp="gjq95710fehe" /> : <div className="flex h-36 w-full items-center justify-center bg-ink-800" data-id="element-1779" data-mp="ljclle90kqp5">
                  <ImageIcon className="h-5 w-5 text-zinc-600" data-id="element-1780" data-mp="qh4mgxrmzn5h" />
                </div>}
              <div className="p-3" data-id="element-1781" data-mp="uk116de22z45">
                <div className="text-[11px] uppercase tracking-wide text-zinc-600" data-id="element-1782" data-mp="ukaplwjgcy7q">
                  {draft.siteUrl.replace(/^https?:\/\//, '') || 'yourband.com'}
                </div>
                <div className="mt-1 line-clamp-2 text-[13px] font-medium text-white" data-id="element-1783" data-mp="14fkwaxksptru">
                  {draft.seoTitle || `${draft.bandName} · Official Site`}
                </div>
              </div>
            </Card>
          </div>

          <Card className="p-4" data-id="element-1784" data-mp="1i8ka8v4xjfxa">
            <h3 className="text-[13.5px] font-semibold text-white" data-id="element-1785" data-mp="1i9bvpd4tbx9u">How long this takes</h3>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-zinc-500" data-id="element-1786" data-mp="1n73tgns52dly">
              Google usually finds a new site within a few days, but ranking for your band name takes two to four weeks,
              and local searches like "country band Fort Worth" take a few months of steady shows and links. Nothing here
              works overnight — publishing dates consistently is what compounds.
            </p>
          </Card>
        </aside>
      </div>
    </div>;
}
function Field({
  label,
  value,
  onChange,
  helper,
  placeholder,
  icon: Icon
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  helper?: string;
  placeholder?: string;
  icon?: React.ComponentType<{
    className?: string;
  }>;
}) {
  return <label className="block" data-id="element-1787" data-mp="1kf3dz980c9sf">
      <span className="mb-1.5 flex items-center gap-1.5 text-[12px] font-medium text-zinc-400" data-id="element-1788" data-mp="1pfzfxh8stlv7">
        {Icon && <Icon className="h-3.5 w-3.5 text-zinc-600" data-id="element-1789" data-mp="3m92npq5hej30" />}
        {label}
      </span>
      <input value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} className="w-full rounded-xl border border-white/[0.1] bg-ink-800 px-3 py-2.5 text-[13px] text-white outline-none placeholder:text-zinc-600 focus:border-[#d97742]/70 focus:ring-2 focus:ring-[#d97742]/15" data-id="element-1790" data-mp="3r7mec3v0sj1p" />
      {helper && <span className="mt-1.5 block text-[11px] leading-relaxed text-zinc-600" data-id="element-1791" data-mp="1zkpkzvzi163n">{helper}</span>}
    </label>;
}